import type { InvestigationRead, SpeechSegment } from '@/domains/types'
import type { SupportedLangCode } from '@/application/i18n/language'
import { markdownToSpeechBlocks } from './markdown-to-speech'
import { splitIntoUtterances } from './split-utterances'
import { resolveContentLang } from './content-lang'

function countWords(text: string): number {
  const trimmed = text.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).length
}

/** Appends every utterance of one markdown field as segments, returning the
 * updated running word cursor (`SpeechSegment.startWord` for the next
 * field). Mutates `segments` in place — internal helper, not exported. */
function pushMarkdownField({
  segments,
  markdown,
  sectionId,
  lang,
  fieldKey,
  wordCursor,
}: {
  segments: SpeechSegment[]
  markdown: string | null | undefined
  sectionId: string | null
  lang: SupportedLangCode
  fieldKey: string
  wordCursor: number
}): number {
  let cursor = wordCursor
  const blocks = markdownToSpeechBlocks(markdown)

  blocks.forEach((block, blockIndex) => {
    const utterances = splitIntoUtterances(block.text, lang)
    utterances.forEach((utterance, utteranceIndex) => {
      const words = countWords(utterance)
      segments.push({
        id: `${sectionId ?? 'investigation'}:${fieldKey}:${blockIndex}:${utteranceIndex}`,
        text: utterance,
        lang,
        kind: block.kind,
        sectionId,
        words,
        startWord: cursor,
      })
      cursor += words
    })
  })

  return cursor
}

/** Appends a plain-text title (investigation name / lead title / fact
 * title — never markdown) as its own heading segment. */
function pushTitle({
  segments,
  title,
  sectionId,
  lang,
  fieldKey,
  wordCursor,
}: {
  segments: SpeechSegment[]
  title: string
  sectionId: string | null
  lang: SupportedLangCode
  fieldKey: string
  wordCursor: number
}): number {
  const words = countWords(title)
  if (words === 0) return wordCursor

  segments.push({
    id: `${sectionId ?? 'investigation'}:${fieldKey}`,
    text: title.trim(),
    lang,
    kind: 'heading',
    sectionId,
    words,
    startWord: wordCursor,
  })
  return wordCursor + words
}

type ReadForSpeechSegments = Pick<InvestigationRead, 'investigation' | 'leads' | 'availableLocales'>

/**
 * Builds the full ordered list of spoken segments for one article: the
 * investigation's own title and description, then for each lead its title
 * and description, then for each of its facts their title and description —
 * exactly the fields `ReadArticleHeader`/`ReadLeadSection`/`ReadFactBlock`
 * render, and nothing else (no categories, author, dates, notes, sources,
 * comments — read-mode.listen spec §2).
 *
 * Each entity's language is resolved independently via `resolveContentLang`
 * (a lead/fact can be authored in a different source language than its
 * parent investigation).
 */
export function buildSpeechSegments(
  read: ReadForSpeechSegments,
  uiLocale: SupportedLangCode,
): SpeechSegment[] {
  const segments: SpeechSegment[] = []
  let wordCursor = 0

  const investigationLang = resolveContentLang(
    read.investigation.sourceLocale,
    read.investigation.sourceLocale,
    uiLocale,
    read.availableLocales,
  )

  wordCursor = pushTitle({
    segments,
    title: read.investigation.name,
    sectionId: null,
    lang: investigationLang,
    fieldKey: 'name',
    wordCursor,
  })

  wordCursor = pushMarkdownField({
    segments,
    markdown: read.investigation.description,
    sectionId: null,
    lang: investigationLang,
    fieldKey: 'description',
    wordCursor,
  })

  for (const entry of read.leads) {
    const leadLang = resolveContentLang(
      entry.lead.sourceLocale,
      read.investigation.sourceLocale,
      uiLocale,
      read.availableLocales,
    )

    wordCursor = pushTitle({
      segments,
      title: entry.lead.title,
      sectionId: entry.lead.id,
      lang: leadLang,
      fieldKey: 'title',
      wordCursor,
    })

    wordCursor = pushMarkdownField({
      segments,
      markdown: entry.lead.description,
      sectionId: entry.lead.id,
      lang: leadLang,
      fieldKey: 'description',
      wordCursor,
    })

    for (const fact of entry.facts) {
      const factLang = resolveContentLang(
        fact.sourceLocale,
        read.investigation.sourceLocale,
        uiLocale,
        read.availableLocales,
      )

      wordCursor = pushTitle({
        segments,
        title: fact.title,
        sectionId: fact.id,
        lang: factLang,
        fieldKey: 'title',
        wordCursor,
      })

      wordCursor = pushMarkdownField({
        segments,
        markdown: fact.description,
        sectionId: fact.id,
        lang: factLang,
        fieldKey: 'description',
        wordCursor,
      })
    }
  }

  return segments
}
